import { useQuery } from "@tanstack/react-query";
import { apiFetch, ApiClientError } from "@/lib/api-client";

export interface InboxVolumeData {
  summary: {
    received: number;
    sent: number;
    read: number;
    failed: number;
    uniqueConversations: number;
  };
  timeseries: Array<{ date: string; received: number; sent: number }>;
  byPlatform: Array<{ platform: string; received: number; sent: number }>;
}

export interface InboxHeatmapData {
  // dow: 1 = Monday .. 7 = Sunday
  buckets: Array<{ dow: number; hour: number; received: number; sent: number; read: number }>;
}

export interface InboxResponseTimeData {
  summary: { medianSeconds: number; p90Seconds: number; p99Seconds: number };
  histogram: Array<{ bucket: string; count: number }>;
}

export interface InboxTopAccountsData {
  accounts: Array<{
    accountId: string;
    displayName: string;
    medianResponseSeconds: number;
    repliedCount: number;
  }>;
}

function useInboxAnalyticsQuery<T>(
  brandId: string,
  path: string,
  fromDate: string,
  toDate: string,
) {
  return useQuery<T | null>({
    queryKey: ["inbox-analytics", brandId, path, fromDate, toDate],
    queryFn: async (): Promise<T | null> => {
      try {
        return await apiFetch<T>(
          `/brands/${brandId}/inbox/analytics/${path}?fromDate=${fromDate}&toDate=${toDate}`,
        );
      } catch (err) {
        if (err instanceof ApiClientError && err.code === "ZERNIO_NOT_CONFIGURED") {
          return null;
        }
        throw err;
      }
    },
    enabled: !!brandId && !!fromDate,
    staleTime: 5 * 60_000,
  });
}

export function useInboxVolume(brandId: string, fromDate: string, toDate: string) {
  return useInboxAnalyticsQuery<InboxVolumeData>(brandId, "volume", fromDate, toDate);
}

export function useInboxHeatmap(brandId: string, fromDate: string, toDate: string) {
  return useInboxAnalyticsQuery<InboxHeatmapData>(brandId, "heatmap", fromDate, toDate);
}

export function useInboxResponseTime(brandId: string, fromDate: string, toDate: string) {
  return useInboxAnalyticsQuery<InboxResponseTimeData>(
    brandId,
    "response-time",
    fromDate,
    toDate,
  );
}

export function useInboxTopAccounts(brandId: string, fromDate: string, toDate: string) {
  return useInboxAnalyticsQuery<InboxTopAccountsData>(
    brandId,
    "top-accounts",
    fromDate,
    toDate,
  );
}
